import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { BaseComponent } from 'src/shared/abstracts/base.abstract';

import { featuresWithPermissions } from '../../../shared/data/permissions';
import { IUser } from '../../../shared/interfaces/users/user.interface';
import { PermissionsService } from '../../../shared/services/internal/permissions.service';
import { OpenMenu } from '../../../shared/store/actions/menu.action';
import { AuthState } from '../../../shared/store/state/auth.state';
import { HomeworkSchoolState } from '../../../shared/store/state/homework-school.state';
import { MealsState } from '../../../shared/store/state/meals.state';
import { MenuState } from '../../../shared/store/state/menu.state';
import { MessagingState } from '../../../shared/store/state/messaging.state';
import { NurseryState } from '../../../shared/store/state/nursery.state';
import { ISidenavChildLink, ISidenavLink } from '../../interfaces/side-nav.interface';
import { EntityTypeEnum } from './../../../shared/enums/entity-type.enum';
import { OrdersState } from './../../../shared/store/state/orders.state';
import { ISchool } from '../../../shared/interfaces';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent extends BaseComponent implements OnInit {
    @Select(MenuState.isOpen) public isMenuOpen$: Observable<boolean>;
    @Select(AuthState.user) public connectedUser$: Observable<IUser>;
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(AuthState.currentSchool) public currentSchool$: Observable<ISchool>;

    @Select(MealsState.getIsMealsSettingsLoaded) public isMealsSettingsLoaded$: Observable<boolean>;
    @Select(NurseryState.getIsNurserySettingsLoaded) public isNurserySettingsLoaded$: Observable<boolean>;
    @Select(HomeworkSchoolState.getIsHomeworkSchoolSettingsLoaded) public homeworkSettingsLoaded$: Observable<boolean>;
    @Select(AuthState.isSchoolSettingsLoaded) public isSchoolSettingsLoaded$: Observable<boolean>;
    @Select(MessagingState.getNbUnreadMessages) public unreadMessagesCount$: Observable<number>;
    @Select(OrdersState.hasHomeworkOrders) public hasHomeworkOrders$: Observable<boolean>;
    @Select(OrdersState.hasMealOrders) public haseMealOrders$: Observable<boolean>;
    @Select(HomeworkSchoolState.hasActiveMonth) public hasActiveMonthForHomeworks$: Observable<boolean>;
    @Select(MealsState.hasActiveMonth) public hasActiveMonthForMeals$: Observable<boolean>;

    @ViewChild('dropdownLink', { static: false }) public dropdown: ElementRef<HTMLElement>;

    public isMenuOpen: boolean;

    public permissions = featuresWithPermissions;
    public entityTypes = EntityTypeEnum;
    public unreadNotif: string = 'new';
    public config: ISidenavLink[] = [
        {
            title: 'NAV.SIDE-NAV.MESSAGING.TITLE',
            link: '/messaging',
            icon: 'i-comment',
            dropdownName: 'navbarDropdown',
            childrens: [
                // {
                //     title: 'NAV.SIDE-NAV.MESSAGING.NEW',
                //     link: '/messaging/new-message',
                //     icon: 'i-pen',
                // },
                {
                    title: 'NAV.SIDE-NAV.MESSAGING.RECEIVED',
                    link: '/messaging/received-messages',
                    icon: 'i-inbox',
                },
                {
                    title: 'NAV.SIDE-NAV.MESSAGING.SENT',
                    link: '/messaging/sent-messages',
                    icon: 'i-paper-plane',
                },
                {
                    title: 'NAV.SIDE-NAV.MESSAGING.DELETED',
                    link: '/messaging/deleted-messages',
                    icon: 'i-archive',
                },
            ],
        },
        {
            title: 'NAV.SIDE-NAV.REPERTOIRE',
            link: '/directory',
            icon: 'i-user-friends',
        },
        {
            title: 'NAV.SIDE-NAV.SHOPPING.TITLE',
            link: '/shopping',
            icon: 'i-store',
            dropdownName: 'navbarDropdownShop',
            permission: featuresWithPermissions.shopping_nav,
            childrens: [
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.WALLET',
                    link: '/shopping/dashboard',
                    icon: 'i-wallet',
                    permission: featuresWithPermissions.wallet_student,
                },
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.SCHOOL-WALLET',
                    link: '/shopping/dashboard',
                    icon: 'i-gaug-solid',
                    permission: featuresWithPermissions.wallet_admin,
                },
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.MEALS',
                    link: '/shopping/meals',
                    icon: 'i-utensils',
                    permission: featuresWithPermissions.meals_nav,
                },
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.NURSERY',
                    link: '/shopping/nursery',
                    icon: 'i-id-card',
                    permission: featuresWithPermissions.nursery_nav,
                },
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.ACTIVITIES',
                    link: '/shopping/activities',
                    icon: 'i-basketball',
                    permission: featuresWithPermissions.activities_nav,
                },
                {
                    title: 'NAV.SIDE-NAV.SHOPPING.HOMEWORK-SCHOOL',
                    link: '/shopping/homework-school',
                    icon: 'i-book-reader',
                    permission: featuresWithPermissions.homework_school_nav,
                },
            ],
        },
        {
            title: 'NAV.SIDE-NAV.CLOUD',
            link: '/cloud',
            icon: 'i-cloud',
            permission: featuresWithPermissions.cloud_nav,
        },
        {
            title: 'NAV.SIDE-NAV.CALENDAR',
            link: '/calendar',
            icon: 'i-calendar',
            v2: true,
            permission: featuresWithPermissions.calendar_nav,
            disabled: false,
        },
        {
            title: 'NAV.SIDE-NAV.SETTINGS',
            link: '/settings',
            icon: 'i-cog',
            permission: featuresWithPermissions.admin_settings_nav,
        },
    ];

    public url: string;

    constructor(private router: Router, private store: Store, private permissionsService: PermissionsService) {
        super();
    }

    public ngOnInit(): void {
        // secondary link data
        this.uns = this.currentSchool$.subscribe((school: ISchool) => {
            const calendarNavLink: ISidenavLink = this.config.find((navLink: ISidenavLink) => navLink.link === '/calendar');
            if (calendarNavLink) {
                calendarNavLink.disabled = !(school.settings.calendar || false);
            }
        });

        this.uns = combineLatest([
            this.connectedUser$,
            this.currentUser$,
            this.isMealsSettingsLoaded$,
            this.isNurserySettingsLoaded$,
            this.homeworkSettingsLoaded$,
            this.isSchoolSettingsLoaded$,
        ])
            .pipe(
                skipWhile(
                    ([
                        connectedUser,
                        currentUser,
                        isMealsSettingsLoaded,
                        isNurserySettingsLoaded,
                        homeworkSettingsLoaded,
                        schoolSettingsLoaded,
                    ]: [IUser, IUser, boolean, boolean, boolean, boolean]) =>
                        currentUser === null ||
                        connectedUser === null ||
                        isMealsSettingsLoaded === false ||
                        isNurserySettingsLoaded === false ||
                        homeworkSettingsLoaded === false ||
                        schoolSettingsLoaded === false
                )
            )
            .subscribe(([connectedUser, currentUser]: [IUser, IUser, boolean, boolean, boolean, boolean]) => {
                if (connectedUser && [6, 7].includes(connectedUser.accessLevel)) {
                    let dashboardAccess: boolean = false;
                    let mealsAccess: boolean = false;
                    let mealsNavLink: ISidenavChildLink;
                    let nurseryAccess: boolean = false;
                    let nurseryNavLink: ISidenavChildLink;
                    let activitiesNavLink: ISidenavChildLink;
                    let activitiesAccess: boolean = false;
                    let homeworkNavLink: ISidenavChildLink;
                    let homeworkAccess: boolean = false;
                    const shopNavLink: ISidenavLink = this.config.find((navLink: ISidenavLink) => navLink.link === '/shopping');
                    if (
                        this.permissionsService.checkPermission(connectedUser.accessLevel, currentUser.accessLevel, shopNavLink.permission)
                    ) {
                        shopNavLink.childrens.map((childNav: ISidenavChildLink) => {
                            if (childNav.link === '/shopping/dashboard') {
                                dashboardAccess = this.permissionsService.checkPermission(
                                    connectedUser.accessLevel,
                                    currentUser.accessLevel,
                                    childNav.permission
                                );
                            } else if (childNav.link === '/shopping/meals') {
                                mealsNavLink = childNav;
                                mealsAccess = this.permissionsService.checkPermission(
                                    connectedUser.accessLevel,
                                    currentUser.accessLevel,
                                    childNav.permission
                                );
                            } else if (childNav.link === '/shopping/nursery') {
                                nurseryNavLink = childNav;
                                nurseryAccess = this.permissionsService.checkPermission(
                                    connectedUser.accessLevel,
                                    currentUser.accessLevel,
                                    childNav.permission
                                );
                            } else if (childNav.link === '/shopping/activities') {
                                activitiesNavLink = childNav;
                                activitiesAccess = this.permissionsService.checkPermission(
                                    connectedUser.accessLevel,
                                    currentUser.accessLevel,
                                    childNav.permission
                                );
                            } else if (childNav.link === '/shopping/homework-school') {
                                homeworkNavLink = childNav;
                                homeworkAccess = this.permissionsService.checkPermission(
                                    connectedUser.accessLevel,
                                    currentUser.accessLevel,
                                    childNav.permission
                                );
                            }
                        });
                        if (!dashboardAccess) {
                            shopNavLink.hasSecondaryLink = true;
                            shopNavLink.secondaryLink = mealsAccess
                                ? mealsNavLink.link
                                : nurseryAccess
                                ? nurseryNavLink.link
                                : activitiesAccess
                                ? activitiesNavLink.link
                                : homeworkAccess
                                ? homeworkNavLink.link
                                : '';
                        }
                    }
                }
            });
        this.url = this.router.url;
        this.uns = this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                this.url = event.url;
            }
        });
        this.uns = this.isMenuOpen$.subscribe((isOpen: boolean) => {
            this.isMenuOpen = isOpen;
            if (!isOpen && this.dropdown.nativeElement.getAttribute('aria-expanded') === 'true') {
                this.dropdown.nativeElement.click();
            }
        });
        this.uns = combineLatest([
            this.hasHomeworkOrders$,
            this.haseMealOrders$,
            this.connectedUser$,
            this.hasActiveMonthForHomeworks$,
            this.hasActiveMonthForMeals$,
        ]).subscribe(
            ([hasHomeworkOrders, hasMealOrders, user, hasActiveMonthForHomeworks, hasActiveMonthForMeals]: [
                boolean,
                boolean,
                IUser,
                boolean,
                boolean
            ]) => {
                if (user?.type === EntityTypeEnum.PARENT) {
                    const linkShop: ISidenavLink = this.config.find((sideNavLink: ISidenavLink) => sideNavLink.link === '/shopping');
                    const sublinkHomework: ISidenavChildLink = linkShop.childrens.find(
                        (linkChild: ISidenavChildLink) => linkChild.link === '/shopping/homework-school'
                    );
                    const sublinkMeal: ISidenavChildLink = linkShop.childrens.find(
                        (linkChild: ISidenavChildLink) => linkChild.link === '/shopping/meals'
                    );
                    if (sublinkHomework) {
                        sublinkHomework.unread = !hasHomeworkOrders && hasActiveMonthForHomeworks;
                    }
                    if (sublinkMeal) {
                        sublinkMeal.unread = !hasMealOrders && hasActiveMonthForMeals;
                    }
                }
            }
        );
    }

    public openMenuDropdown(): void {
        this.store.dispatch(new OpenMenu());
    }

    public get isShopping(): boolean {
        return this.url.includes('/shopping');
    }

    public get isMessaging(): boolean {
        return this.url.includes('/messaging');
    }

    public get isReceivedMessages(): boolean {
        return (this.url.split('/').length === 3 && this.url.split('/')[2] === 'messaging') || this.url.includes('received-messages');
    }
}
